// src/sessionManager.js
import { auth, db } from './firebaseConfig';
import {
  doc, setDoc, getDoc, getDocs, collection, query, orderBy,
  deleteDoc, updateDoc, serverTimestamp, onSnapshot
} from 'firebase/firestore';

const DEVICE_KEY = 'tm_device_id';

// ---- KILL SWITCH (build-time env) ----
export const SESSIONS_ENABLED =
  String(process.env.REACT_APP_ENABLE_SESSIONS || 'false').toLowerCase() === 'true';

// internal timers
let heartbeatTimer = null;
let revokeUnsub = null;

function getOrCreateDeviceId() {
  let id = null;
  try { id = localStorage.getItem(DEVICE_KEY); } catch {}
  if (!id) {
    try {
      id = (crypto?.randomUUID?.() || Math.random().toString(36).slice(2));
      localStorage.setItem(DEVICE_KEY, id);
    } catch {
      id = Math.random().toString(36).slice(2);
    }
  }
  return id;
}
export function currentDeviceId() { return getOrCreateDeviceId(); }

/** Read global maxSessions from Firestore (appConfig/settings.maxSessions), fallback to env or 4 */
export async function getMaxSessions() {
  // Even if sessions are disabled we can return a sane default.
  try {
    const snap = await getDoc(doc(db, 'appConfig', 'settings'));
    const n = Number(snap.data()?.maxSessions);
    if (Number.isFinite(n) && n >= 1) return n;
  } catch {}
  const envN = Number(process.env.REACT_APP_MAX_SESSIONS);
  return Number.isFinite(envN) && envN >= 1 ? envN : 4;
}

// ---- helpers (used only when enabled) ----
async function trimSessions(uid, myDeviceId) {
  const max = await getMaxSessions();
  const col = collection(db, 'users', uid, 'sessions');
  const snap = await getDocs(query(col, orderBy('createdAt', 'desc')));
  if (snap.empty) return;

  let kept = 0;
  const toDelete = [];
  for (const d of snap.docs) {
    const id = d.id;
    if (id === myDeviceId) { kept++; continue; }
    if (kept < max - 1) {
      kept++;
    } else {
      toDelete.push(d.ref);
    }
  }
  if (toDelete.length) await Promise.all(toDelete.map(ref => deleteDoc(ref)));
}

/**
 * Start/update this device’s session and listen for revocation.
 * When SESSIONS_ENABLED=false this is a no-op.
 */
export async function startSession(user, { allowRevokedReset = false } = {}) {
  if (!SESSIONS_ENABLED || !user) return;

  const deviceId = getOrCreateDeviceId();
  const ref = doc(db, 'users', user.uid, 'sessions', deviceId);

  // Check existing
  let existed = false;
  let wasRevoked = false;
  try {
    const existing = await getDoc(ref);
    existed = existing.exists();
    wasRevoked = existed && existing.data()?.revoked === true;
  } catch {}

  if (wasRevoked && !allowRevokedReset) {
    try { await auth.signOut(); } catch {}
    return;
  }

  const payload = {
    deviceId,
    phoneNumber: user.phoneNumber ?? null,
    userAgent: (typeof navigator !== 'undefined' ? navigator.userAgent : '') || '',
    lastSeenAt: serverTimestamp(),
    revoked: false,
  };
  if (!existed) payload.createdAt = serverTimestamp();

  await setDoc(ref, payload, { merge: true });
  await trimSessions(user.uid, deviceId);

  let skipFirst = wasRevoked && allowRevokedReset;
  if (revokeUnsub) revokeUnsub();
  revokeUnsub = onSnapshot(ref, async (snap) => {
    if (skipFirst) { skipFirst = false; return; }
    if (!snap.exists() || snap.data()?.revoked) {
      clearInterval(heartbeatTimer);
      try { await auth.signOut(); } catch {}
    }
  });

  clearInterval(heartbeatTimer);
  heartbeatTimer = setInterval(async () => {
    try { await updateDoc(ref, { lastSeenAt: serverTimestamp() }); } catch {}
  }, 60 * 1000);
}

/** End this device’s session (logout button). No-op if disabled. */
export async function endSession(user) {
  clearInterval(heartbeatTimer);
  if (!SESSIONS_ENABLED || !user) return;
  try { await deleteDoc(doc(db, 'users', user.uid, 'sessions', getOrCreateDeviceId())); } catch {}
  if (revokeUnsub) revokeUnsub();
}

/** Remote sign-out helpers (revocation). No-op if disabled. */
export async function signOutDevice(user, deviceId) {
  if (!SESSIONS_ENABLED || !user || !deviceId) return;
  const ref = doc(db, 'users', user.uid, 'sessions', deviceId);
  try {
    await updateDoc(ref, { revoked: true, revokedAt: serverTimestamp() });
  } catch {
    await setDoc(ref, { revoked: true, revokedAt: serverTimestamp() }, { merge: true });
  }
}

export async function signOutOtherDevices(user) {
  if (!SESSIONS_ENABLED || !user) return;
  const myId = getOrCreateDeviceId();
  const col = collection(db, 'users', user.uid, 'sessions');
  const snap = await getDocs(query(col, orderBy('createdAt', 'desc'))); // order not critical here
  await Promise.all(
    snap.docs
      .filter(d => d.id !== myId)
      .map(d => updateDoc(d.ref, { revoked: true, revokedAt: serverTimestamp() }))
  );
}

export async function signOutAllDevices(user) {
  if (!SESSIONS_ENABLED || !user) return;
  const col = collection(db, 'users', user.uid, 'sessions');
  const snap = await getDocs(col);
  await Promise.all(
    snap.docs.map(d => updateDoc(d.ref, { revoked: true, revokedAt: serverTimestamp() }))
  );
  try { await auth.signOut(); } catch {}
}
